import { Box, Stack, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";

import { AccountSwitcherOptionGroupDesktop } from "./AccountSwitcherOptionGroup";
import { type AccountSwitcherUser, type UserDetails } from "../../Shared/AccountSwitcher/types";
import { Text } from "@src/appV2/redesign/components/Text";

interface AccountSwitcherMenuWorkplaceDesktopProps {
  workplaceDetails: UserDetails;
  onUserClicked: (user: AccountSwitcherUser) => void;
}

export function AccountSwitcherMenuWorkplaceDesktop(
  props: AccountSwitcherMenuWorkplaceDesktopProps
) {
  const { workplaceDetails, onUserClicked } = props;
  const { profile } = useSession();
  const isRebrand = useIsRebrand();
  const currentWorkplaceId = profile?.userId;
  const [searchTerm, setSearchTerm] = useState("");

  const filteredWorkplaces = useMemo(() => {
    if (!searchTerm.trim()) {
      return workplaceDetails.list;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return workplaceDetails.list.filter((workplace) =>
      workplace.title.toLowerCase().includes(lowerSearch)
    );
  }, [workplaceDetails.list, searchTerm]);

  const filteredWorkplaceDetails: UserDetails = useMemo(
    () => ({
      ...workplaceDetails,
      list: filteredWorkplaces,
    }),
    [workplaceDetails, filteredWorkplaces]
  );

  return (
    <Stack>
      <Text
        variant="caption"
        sx={(theme) => ({
          color: theme.palette.navbar?.itemContent,
          paddingX: 4,
          paddingY: 2,
          textTransform: "uppercase",
          opacity: 0.7,
        })}
      >
        Workplaces
      </Text>
      {workplaceDetails.list.length > 1 && (
        <>
          <Box sx={{ paddingX: 2, paddingBottom: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search workplaces..."
              autoComplete="off"
              value={searchTerm}
              sx={(theme) => ({
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isRebrand
                    ? theme.palette.background?.base
                    : theme.palette.background?.inverted?.muted,
                  color: isRebrand
                    ? theme.palette.base?.base
                    : theme.palette.navbar?.itemContentSelected,
                  "& fieldset": {
                    borderColor: isRebrand
                      ? theme.palette.button?.outline?.normal?.borderColor
                      : theme.palette.button?.outline?.inverted?.normal?.borderColor,
                  },
                  "&:hover fieldset": {
                    borderColor: isRebrand
                      ? theme.palette.button?.outline?.hover?.borderColor
                      : theme.palette.button?.outline?.inverted?.hover?.borderColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: isRebrand
                      ? theme.palette.button?.outline?.hover?.borderColor
                      : theme.palette.button?.outline?.inverted?.hover?.borderColor,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  color: isRebrand
                    ? theme.palette.base?.base
                    : theme.palette.navbar?.itemContentSelected,
                  "&::placeholder": {
                    color: isRebrand
                      ? theme.palette.base?.muted
                      : theme.palette.navbar?.itemContent,
                    opacity: 1,
                  },
                },
              })}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
            />
          </Box>
        </>
      )}
      <Box
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <AccountSwitcherOptionGroupDesktop
          userDetails={filteredWorkplaceDetails}
          onUserClicked={onUserClicked}
          loadMoreLabel="Load more workplaces"
          selectedUserId={currentWorkplaceId}
        />
      </Box>
    </Stack>
  );
}
